import * as ImagePicker from 'expo-image-picker';
import Analytics from '@/utils/analytics';
import CameraIcon from '@assets/icons/camera.svg';
import i18n from '@/utils/i18n';
import PlusIcon from '@assets/icons/plus.svg';
import React, { useCallback, useEffect, useState } from 'react';
import { CannedQuestionActionSheet } from './chatbot/canned-question-action-sheet';
import { ChatComposer } from './chat-composer';
import { ChatSendButton } from './chat-send-button';
import { CircledIcon } from '@/components/circled-icon';
import {
  COMPOSER_HEIGHT,
  INPUT_TOOLBAR_HIT_SLOP,
  INPUT_TOOLBAR_PADDING_VERTICAL,
} from './constants';
import { createStyles } from '@/contexts/theme-context';
import { ImagePickerActionSheet } from '@/components/image-picker-action-sheet';
import { InputToolbarProps } from 'react-native-gifted-chat';
import { Message } from './types';
import { Platform, StyleSheet, TouchableOpacity, View } from 'react-native';

export type Props = InputToolbarProps<Message> & {
  isCaseyAi: boolean;
  isSendDisabled: boolean;
  onPressPrompt: (text: string) => void;
  setShowCannedQuestionActionSheet: (show: boolean) => void;
  showCannedQuestionActionSheet: boolean;
};

export const ChatInputToolbar: React.FC<Props> = ({
  isCaseyAi,
  isSendDisabled,
  onPressPrompt,
  setShowCannedQuestionActionSheet,
  showCannedQuestionActionSheet,
  ...props
}) => {
  const [styles, theme] = useStyles();

  const [showImagePickerActionSheet, setShowImagePickerActionSheet] =
    useState(false);

  const sendImage = useCallback(
    (result: ImagePicker.ImagePickerResult) => {
      Analytics.track('chat.image.select');

      const imageURI = result.assets?.[0]?.uri;
      if (imageURI) {
        // @ts-ignore - onSend exists but isn't defined in props
        props.onSend({ image: imageURI });
      }
    },
    [props],
  );

  useEffect(() => {
    if (!isCaseyAi) {
      setShowCannedQuestionActionSheet(false);
    }
  }, [isCaseyAi, setShowCannedQuestionActionSheet]);

  // @ts-ignore - props.text exists but isn't defined in props
  const hasText = Boolean(props.text?.trim());
  return (
    <>
      <View style={styles.base}>
        {isCaseyAi ? (
          <View style={[styles.action, styles.actionLeft]}>
            <TouchableOpacity
              accessibilityLabel={i18n.t(
                'casey_ai.canned_question_action_sheet.open_label',
              )}
              hitSlop={INPUT_TOOLBAR_HIT_SLOP}
              onPress={() => {
                Analytics.track('chat.open_canned_question_sheet_button.press');
                setShowCannedQuestionActionSheet(true);
              }}
            >
              <CircledIcon
                fill={theme.colors.primary}
                Icon={PlusIcon}
                iconHeight={24}
                iconWidth={24}
                style={styles.plusIcon}
              />
            </TouchableOpacity>
          </View>
        ) : (
          <View style={[styles.action, styles.actionLeft]}>
            <TouchableOpacity
              hitSlop={INPUT_TOOLBAR_HIT_SLOP}
              onPress={async () => {
                Analytics.track('chat.image_picker_button.press');
                if (Platform.OS === 'web') {
                  sendImage(await ImagePicker.launchImageLibraryAsync());
                } else {
                  setShowImagePickerActionSheet(true);
                }
              }}
              testID="show-image-picker-button"
            >
              <CameraIcon fill={theme.colors.primary} />
            </TouchableOpacity>
          </View>
        )}
        {/* @ts-ignore */}
        <ChatComposer isSendDisabled={isSendDisabled} {...props} />
        <View
          style={
            hasText
              ? StyleSheet.flatten([styles.action, styles.actionRight])
              : styles.hidden
          }
        >
          <ChatSendButton disabled={isSendDisabled} {...props} />
        </View>
      </View>
      <ImagePickerActionSheet
        isVisible={showImagePickerActionSheet}
        onClose={() => setShowImagePickerActionSheet(false)}
        onSelect={(result) => {
          sendImage(result);
          setShowImagePickerActionSheet(false);
        }}
        title={i18n.t('upload_image')}
      />
      {showCannedQuestionActionSheet ? (
        <CannedQuestionActionSheet
          isVisible
          onClose={() => setShowCannedQuestionActionSheet(false)}
          onSelect={onPressPrompt}
          renderInDraggableSheet={Platform.OS !== 'web'}
        />
      ) : null}
    </>
  );
};

const useStyles = createStyles((theme) => ({
  action: {
    height: COMPOSER_HEIGHT,
    justifyContent: 'center',
    width: 32,
  },
  actionLeft: {
    marginRight: 12,
  },
  actionRight: {
    marginLeft: 12,
    minWidth: 40,
  },
  base: {
    // Anchor everything at the bottom as the text input can grow
    alignItems: 'flex-end',
    backgroundColor: theme.colors.white,
    flexDirection: 'row',
    overflow: 'hidden',
    paddingHorizontal: 16,
    paddingVertical: INPUT_TOOLBAR_PADDING_VERTICAL,
  },
  hidden: {
    // Replaced conditional rendering with opacity: 0 to prevent Yoga layout crashes in RN 0.79+
    // See: https://github.com/facebook/react-native/issues/52349
    // Scoped to Android only as this is an Android-specific crash
    ...Platform.select({
      android: {
        height: 0,
        opacity: 0,
        width: 0,
      },
      default: {
        display: 'none' as const,
      },
    }),
  },
  plusIcon: {
    backgroundColor: theme.colors.background,
    borderWidth: 0,
    height: 32,
    width: 32,
  },
}));
